/**
 * AWS Cognito Authentication Service
 *
 * Wraps amazon-cognito-identity-js to authenticate against a Cognito User Pool
 * using the SRP (Secure Remote Password) protocol. The user's password never
 * leaves the browser in plaintext.
 *
 * This module is only active when VITE_AUTH_MODE=cognito.
 * The Cognito User Pool may reside in a different AWS account (cross-account SSO).
 */

// TYPE-ONLY. A `import type` is erased at build time, so naming these costs
// nothing at runtime and the SDK stays out of this module's static graph.
import type {
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import config from '../config/environment';

/**
 * Load the Cognito SDK on demand, once.
 *
 * Deliberately a dynamic import. AuthContext imports this module eagerly, so
 * a static import put the whole SDK -- and the `buffer` polyfill it depends
 * on -- in the chunk every page loads, including builds where
 * VITE_AUTH_MODE is `local` and none of this is ever called. That polyfill
 * reads a bare `global` at module-evaluation time, which is what took the
 * app down on staging after the Vite migration.
 *
 * The promise is cached rather than the module, so concurrent callers share
 * one request instead of racing to start several.
 */
type CognitoSdk = typeof import('amazon-cognito-identity-js');

let sdk: Promise<CognitoSdk> | null = null;

function loadSdk(): Promise<CognitoSdk> {
  if (!sdk) {
    sdk = import('amazon-cognito-identity-js').catch((err) => {
      // Drop the cache on failure, or the rejection is replayed forever.
      //
      // This matters because the chunk is now fetched when somebody signs
      // in, not when the page loads, and the deploy syncs with `--delete`
      // (frontend-deploy.yml). A tab left open across a deploy therefore
      // asks for a chunk the new version no longer has. Without this the
      // first failure is cached, so every retry in that tab fails the same
      // way -- and not just login: change-password and logout go with it.
      sdk = null;
      throw new Error(
        'Econori has been updated. Please refresh the page and sign in again.',
        { cause: err }
      );
    });
  }
  return sdk;
}

let userPool: CognitoUserPool | null = null;

/**
 * Get or create the Cognito User Pool instance (lazy singleton).
 */
async function getUserPool(): Promise<CognitoUserPool> {
  if (!userPool) {
    if (!config.cognitoUserPoolId || !config.cognitoClientId) {
      throw new Error(
        'Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.'
      );
    }
    const { CognitoUserPool } = await loadSdk();
    userPool = new CognitoUserPool({
      UserPoolId: config.cognitoUserPoolId,
      ClientId: config.cognitoClientId,
    });
  }
  return userPool;
}

/**
 * Authenticate with Cognito using email + password (SRP protocol).
 *
 * Returns the Cognito ID token JWT string on success.
 * The caller should send this token to the backend for validation.
 */
export async function cognitoLogin(email: string, password: string): Promise<string> {
  const pool = await getUserPool();
  const { CognitoUser, AuthenticationDetails } = await loadSdk();
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email.trim().toLowerCase(),
      Pool: pool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email.trim().toLowerCase(),
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken().getJwtToken();
        resolve(idToken);
      },

      onFailure: (err: Error) => {
        // Map Cognito error codes to user-friendly messages
        const errorMessage = mapCognitoError(err);
        reject(new Error(errorMessage));
      },

      newPasswordRequired: () => {
        reject(
          new Error('A password change is required. Please contact your administrator.')
        );
      },

      mfaRequired: () => {
        reject(
          new Error('Multi-factor authentication is required but not yet supported. Please contact your administrator.')
        );
      },

      totpRequired: () => {
        reject(
          new Error('TOTP authentication is required but not yet supported. Please contact your administrator.')
        );
      },
    });
  });
}

/**
 * Sign out the current Cognito user (clears local Cognito session tokens).
 */
export async function cognitoLogout(): Promise<void> {
  try {
    const pool = await getUserPool();
    const currentUser = pool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
  } catch {
    // Ignore errors during logout — the user is leaving anyway
  }
}

/**
 * Change password for the currently authenticated Cognito user.
 *
 * Requires an active Cognito session (user must be logged in).
 */
export async function cognitoChangePassword(oldPassword: string, newPassword: string): Promise<void> {
  const pool = await getUserPool();
  return new Promise((resolve, reject) => {
    const currentUser = pool.getCurrentUser();

    if (!currentUser) {
      reject(new Error('No authenticated user found. Please log in again.'));
      return;
    }

    // getSession is required to refresh/validate the session before calling changePassword
    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(new Error('Session expired. Please log in again.'));
        return;
      }

      currentUser.changePassword(oldPassword, newPassword, (err, _result) => {
        if (err) {
          const errorMessage = mapCognitoError(err as Error & { code?: string });
          reject(new Error(errorMessage));
          return;
        }
        resolve();
      });
    });
  });
}

/**
 * Initiate forgot password flow — sends a verification code to the user's email.
 */
export async function cognitoForgotPassword(email: string): Promise<void> {
  const pool = await getUserPool();
  const { CognitoUser } = await loadSdk();
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email.trim().toLowerCase(),
      Pool: pool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (err: Error) => {
        const errorMessage = mapCognitoError(err as Error & { code?: string });
        reject(new Error(errorMessage));
      },
    });
  });
}

/**
 * Confirm a new password using the verification code from cognitoForgotPassword.
 */
export async function cognitoResetPassword(
  email: string,
  verificationCode: string,
  newPassword: string
): Promise<void> {
  const pool = await getUserPool();
  const { CognitoUser } = await loadSdk();
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email.trim().toLowerCase(),
      Pool: pool,
    });

    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err: Error) => {
        const errorMessage = mapCognitoError(err as Error & { code?: string });
        reject(new Error(errorMessage));
      },
    });
  });
}

/**
 * Map Cognito SDK error codes to user-friendly messages.
 */
function mapCognitoError(err: Error & { code?: string }): string {
  switch (err.code) {
    case 'NotAuthorizedException':
      return 'Invalid email or password';
    case 'UserNotFoundException':
      return 'Invalid email or password';
    case 'UserNotConfirmedException':
      return 'Your account has not been confirmed. Please check your email.';
    case 'PasswordResetRequiredException':
      return 'A password reset is required. Please contact your administrator.';
    case 'TooManyRequestsException':
      return 'Too many login attempts. Please try again later.';
    case 'NetworkError':
      return 'Unable to connect to the authentication service. Please check your internet connection.';
    default:
      return err.message || 'Authentication failed. Please try again.';
  }
}
